import { sanitiseURL, buildQueryString } from '@/utils/urlHelper';
import { smartFocus, loadThemeColor } from '@/utils/uiHelper';
import { isDevelopment } from '@/utils/environment';
import Raven from 'raven-js';

const getEmptyFatalError = () => ({
  error: '', // main message
  message: '', // subtitle
  explanation: '', // HTML content

  appError: '', // code for app login
  appErrorExplanation: '', // explanation for app login
});

// initial state
const initialState = {
  // available types: account, generic, app
  loginType: '',

  // available types: standard, saml-first, saml-email
  loginState: 'standard',

  // meta contains current metadata that may affect rendering
  // or behaviour
  meta: {
    productCode: '',
    hasLocalStorage: true,
    isIframe: false,
    source: '',
    product: '',
    extra: '',
    admin: false,
    ignoreInstallations: '',
    generic: false,
  },

  loggedIn: false,

  fatalError: getEmptyFatalError(),

  samlBaseURL: '',
  afterLoginURL: '',
  formState: '',

  // appInfo stores app authentication info
  appInfo: {
    redirect_uri: '',
    state: '',
    client_id: '',
    scope: [],
    install: false,
    nonce: '',
  },

  waf: null,

  selectedAccount: {
    installation: {
      apiEndPoint: '',
    },
  },
};

// getters
const getters = {
  twAuthBaseURL: (state, getters, rootState) => { // eslint-disable-line
    if (isDevelopment) return 'https://tw-auth-dev.teamwork.com';
    return rootState.site.siteInfo.twAuthBaseURL;
  },
  googleSSOURL: (state, getters) => { // eslint-disable-line
    const url = `${getters.twAuthBaseURL}/launchpad/googleSSO/start`;
    const params = {};

    if (state.afterLoginURL) params.retURL = state.afterLoginURL;

    if (state.loginType === 'app') {
      params.appLoginRedirectURI = state.appInfo.redirect_uri;
      if (state.appInfo.state) params.state = state.appInfo.state;
      if (state.appInfo.client_id) params.appLoginClientID = state.appInfo.client_id;
      if (state.appInfo.scope) params.appLoginScope = state.appInfo.scope;
      if (state.appInfo.install) params.install = state.appInfo.install;
      if (state.appInfo.nonce) params.nonce = state.appInfo.nonce;
      if (state.meta.ignoreInstallations) params.ignoreInstallations = state.meta.ignoreInstallations; // eslint-disable-line
    }

    if (state.meta.source) params.source = state.meta.source;
    if (state.meta.product) params.product = state.meta.product;
    if (state.meta.extra) params.extra = state.meta.extra;

    return `${url}?${buildQueryString(params)}`;
  },
  samlURL: (state) => {
    const url = state.samlBaseURL.length ? `${state.samlBaseURL}launchpad/saml/start` : '/launchpad/saml/start';
    const params = {};

    switch (state.loginType) { // eslint-disable-line
      case 'account':
      case 'generic':
        if (state.afterLoginURL) params.SAMLAfterLoginURL = state.afterLoginURL;
        break;
      case 'app':
        params.appAuth = true;
        params.redirect_uri = state.appInfo.redirect_uri;
        if (state.appInfo.state) params.state = state.appInfo.state;
        if (state.appInfo.scope) params.scope = state.appInfo.scope;
        if (state.appInfo.client_id) params.client_id = state.appInfo.client_id;
        if (state.appInfo.install) params.install = state.appInfo.install;
        if (state.appInfo.nonce) params.nonce = state.appInfo.nonce;
        if (state.meta.ignoreInstallations) params.ignoreInstallations = state.meta.ignoreInstallations; // eslint-disable-line
        break;
    }

    if (state.meta.source) params.source = state.meta.source;
    if (state.meta.product) params.product = state.meta.product;
    if (state.meta.extra) params.extra = state.meta.extra;

    return `${url}?${buildQueryString(params)}`;
  },
  bodyClasses: (state) => {
    const classes = [];
    if (state.meta.isIframe) classes.push('in-iframe');
    if (state.meta.source) classes.push(`source-${state.meta.source}`);
    if (state.meta.product) classes.push(`product-${state.meta.product}`);
    if (state.meta.extra && typeof state.meta.extra === 'string') {
      state.meta.extra.split(',').forEach((s) => {
        classes.push(`extra-${s}`);
      });
    }
    return classes.join(' ');
  },
  allowStandardLogin: (state, getters, rootState) => { // eslint-disable-line
    if (state.meta.admin) return true;
    if (state.loginState === 'saml-first') return !rootState.site.siteInfo.sso.hideStandardLoginLink;
    return rootState.site.siteInfo.displayOptions.allowStandardLogin;
  },
};

// actions
const actions = {
  setupBasicInfo({ commit, dispatch }, { query, params }) {
    const host = window.location.host;
    const generic = host === 'www.teamwork.com' || host === 'teamwork.com' || query.generic
      || (query.gad && host.indexOf('tw-auth') > -1)
      || host === 'staging.teamwork.com' || host === 'www.staging.teamwork.com';

    const appInfo = {};
    if (query.redirect_uri) appInfo.redirect_uri = query.redirect_uri;
    if (query.state) appInfo.state = query.state;
    if (query.client_id) appInfo.client_id = query.client_id;
    if (query.install) appInfo.install = !!query.install;
    if (query.scope) appInfo.scope = query.scope.split(/[\s,]+/);
    if (query.nonce) appInfo.nonce = query.nonce;

    let loginType = 'account';
    if (generic) {
      loginType = 'generic';
      dispatch('loadAwsWaf');
    }
    if (appInfo.redirect_uri) loginType = 'app';

    let afterLoginURL = sanitiseURL(query.continue, loginType === 'account');
    if (loginType === 'account' && !afterLoginURL) {
      afterLoginURL = window.location.href.replace(/\/launchpad(.*)/, '');
      if (params.productCode && params.productCode !== 'projects') {
        afterLoginURL += `/${params.productCode}`;
      } else {
        afterLoginURL += '/app';
      }
    }

    const meta = {
      isIframe: false,
      hasLocalStorage: true,
      generic,
    };
    if (params.productCode) meta.productCode = params.productCode;
    if (query.source) meta.source = query.source;
    if (query.product) meta.product = query.product;
    if (query.extra) meta.extra = query.extra;
    if (query.ignore_installations) meta.ignoreInstallations = query.ignore_installations;

    // check if in iframe
    try {
      meta.isIframe = window.self !== window.top;
    } catch (e) {
      meta.isIframe = true;
    }

    // check if localStorage is supported, as Safari
    // throws errors when in private browsing mode.
    try {
      localStorage.getItem('test');
    } catch (err) {
      meta.hasLocalStorage = false;
    }

    if (meta.hasLocalStorage) loadThemeColor();
    if (query.admin) meta.admin = true;

    window.addEventListener('storage', (eventInfo) => {
      if (eventInfo.key === 'loggedIn') {
        dispatch('setLoggedIn', {
          loggedIn: true,
          ignoreLS: true,
        });
      }
    });

    commit('setBasicInfo', { loginType, appInfo, meta, afterLoginURL });
    if (meta.isIframe) dispatch('setupIFrame', meta.isIframe);
  },
  appendExperimentTracking({ state, commit }, { campaign, variation }) {
    const continueURL = state.afterLoginURL || '';
    const parameter = `utm_content=vwo-${campaign}-${variation}`;
    let url;

    if (continueURL.includes('?')) url = `${continueURL}&${parameter}`;
    else if (continueURL.endsWith('/')) url = `${continueURL}?${parameter}`;
    else url = `${continueURL}/?${parameter}`;

    commit('setAfterLoginURL', url);
  },
  setupIFrame({ state, commit }, isIframe) {
    if (!isIframe) return;

    document.body.style.background = 'transparent';
    document.body.classList.add('in-iframe');

    window.addEventListener('beforeunload', () => {
      window.parent.postMessage('pageChanged', '*');
    });

    const pageResized = () => window.parent.postMessage('pageResized', '*');
    document.addEventListener('transitionend', pageResized);
    document.addEventListener('webkitTransitionEnd', pageResized);
    document.addEventListener('oTransitionEnd', pageResized);
  },
  async loadAwsWaf({ commit }) {
    if (!process.env.WAFJS_URI) return;
    const wafjs = document.createElement('script');
    wafjs.src = process.env.WAFJS_URI;
    wafjs.setAttribute('defer', '');
    document.head.appendChild(wafjs);
    wafjs.onabort = () => Promise.reject(new Error("Couldn't load WAF"));
    wafjs.onerror = wafjs.onabort;
    wafjs.onload = () => {
      commit('setWAF', window.AwsWafIntegration);
      return Promise.resolve();
    };
  },
  async getWafToken() {
    try {
      if (!process.env.WAFJS_URI) return null;
      return window.AwsWafIntegration.getToken();
    } catch (e) {
      Raven.captureException(e, { fingerprint: ['waf library not loaded'] });
      return null;
    }
  },
};

// export `mutations` as a named export
export const mutations = {
  setLoggedIn(state, { loggedIn, userId, ignoreLS }) {
    state.loggedIn = loggedIn;
    if (loggedIn && state.meta.hasLocalStorage && !ignoreLS) {
      localStorage.setItem('loggedIn', JSON.stringify({
        userId,
        dateTimeUTC: new Date().toISOString(),
      }));
      // ensure we wipe any previous stale sessionTimeout keys on fresh login
      localStorage.removeItem('twProjects-sessionTimeout');
    }
  },
  setBasicInfo(state, { loginType, appInfo, meta, afterLoginURL }) {
    if (appInfo && typeof appInfo === 'object') {
      state.appInfo = Object.assign(state.appInfo, appInfo);
    }
    state.meta = Object.assign(state.meta, meta);
    state.loginType = loginType;
    state.afterLoginURL = afterLoginURL;
  },
  showFatalError(state, errorData) {
    state.fatalError = Object.assign(state.fatalError, errorData);
  },
  resetFatalError(state) {
    state.fatalError = getEmptyFatalError();
  },
  setSAMLBaseURL(state, url) {
    state.samlBaseURL = url;
  },
  setLoginState(state, loginState) {
    state.loginState = loginState;
    smartFocus();
  },
  setSelectedAccount(state, { installation }) {
    state.selectedAccount.installation = installation;
  },
  setFormState(state, formState) {
    state.formState = formState;
  },
  setWAF(state, waflib) {
    state.waf = waflib;
  },
  setAfterLoginURL(state, url) {
    state.afterLoginURL = url;
  },
};

export default {
  state: initialState,
  getters,
  actions,
  mutations,
};



// WEBPACK FOOTER //
// ./src/store/modules/login.js